import ky from 'ky'

import { isSuccessCode, type ApiCode } from '@/api/schemas/common'
import { useAuthStore } from '@/stores/auth.store'

import { ApiError } from './api-error'
import { handleSessionExpired } from './auth-session'
import { authStorage, normalizeBearerToken } from './auth-storage'

export const API_PREFIX = `${import.meta.env.VITE_API_BASE_URL.replace(/\/$/, '')}/api/v1/`

let refreshPromise: Promise<string | null> | null = null

/**
 * Exchanges the stored refresh token for a fresh access token, updating both
 * persistent storage and the in-memory auth store (which also drives the SSE
 * presence probe's reconnect with the new token). Concurrent callers share a
 * single in-flight refresh. Returns the new access token, or null on failure.
 *
 * Exported so proactive refresh (see `useTokenAutoRefresh`) reuses the exact
 * same logic as the reactive 401 path.
 */
export const refreshAccessToken = async (): Promise<string | null> => {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const refresh = authStorage.getRefresh()
      if (!refresh) return null

      const res = await ky
        .post(`${API_PREFIX}auth/refresh-token`, {
          searchParams: { refreshToken: refresh },
        })
        .json<{
          code: ApiCode
          data: {
            accessToken: string
            refreshToken: string
            expireTime?: number
            expiresIn?: number
            tokenType?: string
          }
          message?: string
          msg?: string
        }>()

      if (!isSuccessCode(res.code)) return null
      const expiresInSec = res.data.expireTime ?? res.data.expiresIn
      authStorage.set(res.data.accessToken, res.data.refreshToken, expiresInSec)
      const accessToken = normalizeBearerToken(res.data.accessToken)
      useAuthStore.getState().setTokens(accessToken)
      return accessToken
    } catch {
      return null
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

export const http = ky.create({
  prefixUrl: API_PREFIX,
  timeout: 30_000,
  retry: { limit: 0 },
  hooks: {
    beforeRequest: [
      (request) => {
        const token = authStorage.getAccess()
        if (token)
          request.headers.set(
            'Authorization',
            `Bearer ${normalizeBearerToken(token)}`,
          )
      },
    ],
    afterResponse: [
      async (request, _options, response) => {
        if (response.status === 401) {
          // First 401: try a single refresh + retry.
          if (!request.headers.get('x-retry')) {
            const accessToken = await refreshAccessToken()
            if (accessToken) {
              request.headers.set(
                'Authorization',
                `Bearer ${normalizeBearerToken(accessToken)}`,
              )
              request.headers.set('x-retry', '1')
              return ky(request)
            }
          }
          // Refresh failed, or the retried request still got 401.
          handleSessionExpired()
          return response
        }

        if (!response.ok) {
          const body = (await response
            .clone()
            .json()
            .catch(() => ({}))) as {
            code?: ApiCode
            message?: string
            msg?: string
          }
          throw new ApiError(
            response.status,
            body.code ?? 'B0001',
            body.msg ?? body.message ?? response.statusText,
          )
        }

        return response
      },
      async (_request, _options, response) => {
        const contentType = response.headers.get('content-type')
        if (!contentType?.includes('application/json')) return response

        const cloned = response.clone()
        const body = (await cloned.json()) as {
          code?: ApiCode
          message?: string
          msg?: string
        }
        if (body.code !== undefined && !isSuccessCode(body.code)) {
          throw new ApiError(
            response.status,
            body.code,
            body.message ?? body.msg ?? '业务错误',
          )
        }

        return response
      },
    ],
  },
})
